import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import DnaHelix from "@/components/DnaHelix";

const GATE_KEY = "gr_entry_accepted_v3";
const OPEN_PATHS = ["/terms", "/privacy", "/research-use", "/verify-email"];

export default function EntryGate() {
    const location = useLocation();
    const [accepted, setAccepted] = useState(() => localStorage.getItem(GATE_KEY) === "true");
    const [step, setStep] = useState("age"); // age | research
    const [denied, setDenied] = useState(false);
    const [researchConfirmed, setResearchConfirmed] = useState(false);
    const isOpenPath = OPEN_PATHS.includes(location.pathname);
    const visible = !accepted && !isOpenPath;

    useEffect(() => {
        if (visible) {
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = "";
            };
        }
    }, [visible]);

    const accept = () => {
        localStorage.setItem(GATE_KEY, "true");
        window.dispatchEvent(new Event("gr-gate-accepted"));
        setAccepted(true);
    };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    key="entry-gate"
                    data-testid="entry-gate"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.5 } }}
                    className="fixed inset-0 z-[100] bg-ink overflow-y-auto"
                >
                    <div className="relative min-h-screen grid lg:grid-cols-2">
                        {/* Editorial brand panel */}
                        <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-charcoal px-14 py-16">
                            <DnaHelix className="absolute -right-20 top-1/2 -translate-y-1/2 h-[540px] w-auto text-white/10 pointer-events-none" />
                            <div className="relative">
                                <p className="font-serif text-3xl tracking-[0.08em] text-[#F8F4EE]">GRAYHAVEN</p>
                                <p className="mt-1.5 text-[0.6rem] uppercase tracking-[0.42em] text-gold">Research</p>
                            </div>
                            <div className="relative mx-auto w-full max-w-[19rem]">
                                <div className="absolute -inset-3 border border-gold/30" aria-hidden="true" />
                                <img
                                    src="/assets/products/ghr-2t.jpg"
                                    alt="Grayhaven Research vial"
                                    className="relative w-full aspect-[4/5] object-cover"
                                />
                            </div>
                            <div className="relative">
                                <div className="h-px w-14 bg-gold/70" aria-hidden="true" />
                                <p className="mt-5 text-[0.6rem] uppercase tracking-[0.25em] text-slate-400 leading-loose">
                                    Precision · Purity · Purpose
                                    <span className="block">For laboratory research use only</span>
                                </p>
                            </div>
                        </div>

                        {/* Gate content */}
                        <div className="relative flex items-center justify-center px-6 py-14">
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background:
                                        "radial-gradient(ellipse 70% 50% at 50% 20%, rgba(166,143,108,0.16), transparent 70%)",
                                }}
                                aria-hidden="true"
                            />
                            <div className="relative max-w-md w-full text-center">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <img
                                        src="/assets/grayhaven-logo-dna.png"
                                        alt="Grayhaven Research"
                                        data-testid="gate-logo"
                                        className="w-48 sm:w-56 mx-auto lg:hidden"
                                    />
                                    <div className="wine-rule mx-auto mt-6" />
                                </motion.div>

                            {!denied && step === "age" && (
                                <motion.div
                                    key="age-step"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                                    data-testid="gate-age-step"
                                >
                                    <p className="eyebrow mt-8">Research Use Only</p>
                                    <h1 className="mt-5 font-serif uppercase text-2xl sm:text-3xl font-medium tracking-[0.04em] text-brand leading-snug">
                                        Are you 21 years of age or older?
                                    </h1>
                                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setStep("research")}
                                            data-testid="gate-age-yes-btn"
                                            className="w-full sm:w-auto bg-wine text-white px-12 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-wine-deep transition-colors duration-300"
                                        >
                                            Yes, Enter
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDenied(true)}
                                            data-testid="gate-exit-btn"
                                            className="w-full sm:w-auto bg-transparent border border-taupe/70 text-slate-500 px-12 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold hover:border-slate-400 hover:text-slate-700 transition-colors duration-300"
                                        >
                                            No, Exit
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {!denied && step === "research" && (
                                <motion.div
                                    key="research-step"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                    data-testid="gate-research-step"
                                >
                                    <p className="eyebrow mt-8">Research Use Acknowledgment</p>
                                    <h1 className="mt-5 font-serif uppercase text-2xl sm:text-3xl font-medium tracking-[0.04em] text-brand leading-snug">
                                        Before you enter
                                    </h1>
                                    <p className="mt-6 text-xs sm:text-sm leading-relaxed text-mist max-w-md mx-auto">
                                        The materials and information presented on this website are intended solely
                                        for legitimate laboratory research purposes. They are not intended for human
                                        or veterinary use, consumption, administration, diagnosis, treatment, or
                                        prevention of disease.
                                    </p>
                                    <div className="mt-8 text-left max-w-md mx-auto">
                                        <label className="flex items-start gap-3 cursor-pointer bg-[#FDFAF5] border border-taupe/60 rounded-sm px-5 py-4 hover:border-wine/40 transition-colors duration-300" data-testid="gate-research-check">
                                            <input
                                                type="checkbox"
                                                checked={researchConfirmed}
                                                onChange={(e) => setResearchConfirmed(e.target.checked)}
                                                className="mt-0.5 h-4 w-4 shrink-0 accent-[#7A2230]"
                                            />
                                            <span className="text-xs leading-relaxed text-slate-600">
                                                I understand and acknowledge that this website and its materials are
                                                for legitimate laboratory research purposes only.
                                            </span>
                                        </label>
                                    </div>
                                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                                        <button
                                            type="button"
                                            onClick={accept}
                                            disabled={!researchConfirmed}
                                            data-testid="gate-enter-btn"
                                            className="w-full sm:w-auto bg-wine text-white px-10 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-wine-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300"
                                        >
                                            Enter Grayhaven Research
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDenied(true)}
                                            data-testid="gate-research-exit-btn"
                                            className="w-full sm:w-auto bg-transparent border border-taupe/70 text-slate-500 px-10 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold hover:border-slate-400 hover:text-slate-700 transition-colors duration-300"
                                        >
                                            Exit
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {!denied && (
                                <div className="mt-12 flex items-center justify-center gap-3 text-[0.6rem] uppercase tracking-[0.18em] text-slate-400">
                                    <Link to="/terms" data-testid="gate-terms-link" className="hover:text-wine transition-colors duration-300">
                                        Terms &amp; Conditions
                                    </Link>
                                    <span className="text-gold/50">·</span>
                                    <Link to="/research-use" data-testid="gate-research-use-link" className="hover:text-wine transition-colors duration-300">
                                        Research Use Policy
                                    </Link>
                                    <span className="text-gold/50">·</span>
                                    <Link to="/privacy" data-testid="gate-privacy-link" className="hover:text-wine transition-colors duration-300">
                                        Privacy Policy
                                    </Link>
                                </div>
                            )}

                            {denied && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6 }}
                                    data-testid="gate-exit-panel"
                                >
                                    <h1 className="mt-10 font-serif uppercase text-2xl sm:text-3xl font-medium tracking-[0.04em] text-brand leading-snug">
                                        Access Restricted
                                    </h1>
                                    <p className="mt-6 text-xs sm:text-sm leading-relaxed text-mist max-w-md mx-auto">
                                        This website is intended for adults aged 21 or older seeking materials for
                                        legitimate laboratory research. You are welcome to return when this applies to
                                        you.
                                    </p>
                                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setDenied(false)}
                                            data-testid="gate-return-btn"
                                            className="w-full sm:w-auto bg-transparent border border-gold/60 text-gold px-10 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold hover:bg-gold/10 transition-colors duration-300"
                                        >
                                            Return
                                        </button>
                                        <a
                                            href="https://www.google.com"
                                            data-testid="gate-leave-link"
                                            className="w-full sm:w-auto text-center border border-taupe/70 text-slate-500 px-10 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold hover:border-slate-400 hover:text-slate-700 transition-colors duration-300"
                                        >
                                            Leave Website
                                        </a>
                                    </div>
                                </motion.div>
                            )}
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
